import { TableOptions, TableVisibility, DatatableInterface, TableHeader } from '@/components/data-table/DataTable';
import { Component, Prop } from 'vue-property-decorator';
import AbstractDatatable from '@/components/CustomDatatable/AbstractDatatable';
import { PurchaseOrderSummary } from '@/models/PurchaseOrderSummary';
import { Category } from '@/models/Category';
import { Nmbr } from '@atabix/core';
import { Supplier } from '@/models/Supplier';
import { isEqual } from 'lodash';
import { thumbnailTransformer } from '@/support/ThumbnailTransformer';

@Component<PurchaseOrderOrderSummaryDatatable>({})
export default class PurchaseOrderOrderSummaryDatatable extends AbstractDatatable implements DatatableInterface {
  // #region @VModels
  // #endregion

  // #region @Props

  @Prop({ default: '' })
  protected purchaseOrderId!: string;

  // #endregion

  // #region @Refs
  // #endregion

  // #region Class properties

  protected defaultPage = 1;

  protected tableVisibility: TableVisibility = {
    checkboxes: false,
    limit: true,
    pagination: true,
    search: false,
    title: false,
    total: true,
  };

  protected datatableSizesOnCurrentPage: string[] = [];

  // #endregion

  // #region Lifecycle Hooks / Init
  // #endregion

  // #region Class methods

  protected onDataTableDataLoaded(data: undefined | null | PurchaseOrderSummary[]): void {
    if (!data) {
      if (isEqual(this.datatableSizesOnCurrentPage, [])) { return; }

      this.datatableSizesOnCurrentPage = [];

      return;
    }

    const sizesGroups = data
      .map((purchaseOrderSummary) => purchaseOrderSummary.size)
      .filter((sizeGroup) => !! sizeGroup);

    const sizesWithDuplicates = sizesGroups
      .map((sizeGroup) =>
        sizeGroup
          ? Object.keys(sizeGroup)
          : [])
      .flat();

    const sizes = [...new Set(sizesWithDuplicates)];

    if (isEqual(this.datatableSizesOnCurrentPage, sizes)) { return; }

    this.datatableSizesOnCurrentPage = sizes;
  }

  // #endregion

  // #region Async methods
  // #endregion

  // #region Getters & Setters

  protected get page() {
    const result = localStorage.getItem(`${this.$route.name}_page`);
    return result ? Number(result) : this.defaultPage;
  }

  protected set page(page: number) {
    localStorage.setItem(`${this.$route.name}_page`, page.toString());
    this.defaultPage = page;
  }

  protected get tableOptions(): TableOptions {
    const purchaseOrdersummaryModel = new PurchaseOrderSummary();
    purchaseOrdersummaryModel.$purchaseOrderId = this.purchaseOrderId;

    return {
      model: purchaseOrdersummaryModel,
      name: {
        singular: 'Purchase order summary',
        plural: 'Purchase order summary'
      },
      headers: [
        {
          value: 'image_url',
          text: '',
          transform: (imageUrl: undefined | null | string) =>
            imageUrl
              ? thumbnailTransformer(imageUrl)
              : '',
        },
        {
          value: 'supplier',
          text: 'Supplier / Brand',
          transform: (supplier: undefined | null | string | Supplier, item: PurchaseOrderSummary) => {
            let supplierName = '';
            if (supplier && typeof supplier === 'string') { supplierName = supplier; }
            if (supplier && typeof supplier !== 'string') { supplierName = supplier.name || ''; }

            let brandName = '';
            if (item.brand && typeof item.brand === 'string') { brandName = item.brand; }
            if (item.brand && typeof item.brand !== 'string') { brandName = item.brand.name || ''; }

            return [
              supplierName,
              brandName,
            ]
              .filter((value) => !! value)
              .join('<br/>')
          },
        },
        {
          value: 'product_name',
          text: 'Product name',
          class: 'font-semibold',
        },
        {
          value: 'category',
          text: 'Category',
          transform: (categories: undefined | null | Category[]) =>
            categories
              ? categories
                  .map((category) => category.name)
                  .join('<br/>')
              : '',
        },
        {
          value: 'color',
          text: 'Color / Supplier color',
          transform: (color: undefined | null | string, item: PurchaseOrderSummary) =>
            [
              color
                ? color
                : '',
              item.supplier_color
                ? item.supplier_color
                : '',
            ]
              .filter((value) => !! value)
              .join('<br/>'),
        },
        {
          value: 'season',
          text: 'Season',
        },
        {
          value: 'retail_price',
          text: 'Price',
          transform: (retailPrice: undefined | null | string, item: PurchaseOrderSummary) =>
            [
              `<span class="white-space-no-wrap">R: <span style="display: inline-block; min-width: 60px;">${Nmbr.toCurrency(retailPrice || '')}</span></span>`,
              `<span class="white-space-no-wrap">B: <span style="display: inline-block; min-width: 60px;">${Nmbr.toCurrency(
                item.buy_price
                  ? item.buy_price
                  : ''
              )}</span></span>`,
            ]
              .filter((value) => !! value)
              .join('<br/>'),
          class: 'text-right',
          align: 'right',
        },
        {
          value: 'total_quantity',
          text: 'Total QTY',
          class: 'font-semibold text-right',
          align: 'right',
        },
        {
          value: 'total_value',
          text: 'Total value',
          transform: (totalValue: undefined | null | string) => Nmbr.toCurrency(totalValue || ''),
          class: 'font-semibold text-right',
          align: 'right',
          currencyFormat: true,
        },
        ...this.datatableSizesOnCurrentPage.map((size) => ({
          value: `size__${size}`,
          text: size,
          transform: (value: undefined, item: PurchaseOrderSummary) =>
            item.size
            && item.size[size]
              ? item.size[size]
              : '',
        })),
      ],
      actions: [],
      filters: [],
      showTotals: true,
    }
  }
  // #endregion

  // #region @Watchers
  // #endregion
}

// #region Enums
// #endregion

// #region Types
// #endregion

// #region Interfaces
// #endregion
